import React, { useState, useEffect } from 'react';
import { Power, Search, Filter } from 'lucide-react';
import api from '../../api';

/**
 * Feature Flags Management Component
 * Allows admins to enable/disable features dynamically
 */
const FeatureFlagsComponent = () => {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const adminUsername = 'admin'; // TODO: Get from auth context

  useEffect(() => {
    fetchFeatures();
  }, []);

  const fetchFeatures = async () => {
    try {
      const response = await api.get('/admin/features');
      setFeatures(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const toggleFeature = async (featureKey, currentStatus) => {
    try {
      const newStatus = !currentStatus;
      await api.put(`/admin/features/${featureKey}/toggle`, null, {
        params: { enabled: newStatus, adminUsername }
      });

      // Update local state
      setFeatures(prev => prev.map(f =>
        f.featureKey === featureKey ? { ...f, isEnabled: newStatus } : f
      ));

      alert(`Feature ${newStatus ? 'enabled' : 'disabled'} successfully!`);
    } catch (error) {
      alert('Failed to toggle feature');
    }
  };

  const filteredFeatures = features.filter(f => {
    const matchesSearch = f.featureName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         f.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'ALL' || f.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const categories = ['ALL', ...new Set(features.map(f => f.category))];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-3xl font-bold text-gray-800">Feature Flags</h2>
        <button
          onClick={fetchFeatures}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
        >
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search features..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-gray-800"
            />
          </div>

          {/* Category Filter */}
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-600" />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-gray-800"
            >
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Features List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredFeatures.map((feature) => (
            <div
              key={feature.id}
              className={`bg-white rounded-lg shadow p-6 ${
                feature.featureKey === 'CHAT_ENABLED' ? 'ring-2 ring-blue-500' : ''
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-gray-800 mb-1">{feature.featureName}</h3>
                    {feature.featureKey === 'CHAT_ENABLED' && (
                      <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                        Important
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 mb-2">{feature.featureKey}</p>
                  <p className="text-sm text-gray-600">{feature.description}</p>
                </div>
              </div>

              <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
                <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700">
                  {feature.category}
                </span>

                <button
                  onClick={() => toggleFeature(feature.featureKey, feature.isEnabled)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition ${
                    feature.isEnabled
                      ? 'bg-green-500 hover:bg-green-600 text-white'
                      : 'bg-gray-300 hover:bg-gray-400 text-gray-700'
                  }`}
                >
                  <Power className="w-4 h-4" />
                  {feature.isEnabled ? 'Enabled' : 'Disabled'}
                </button>
              </div>

              {feature.updatedBy && (
                <p className="text-xs text-gray-400 mt-2">
                  Last updated by: {feature.updatedBy}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {filteredFeatures.length === 0 && !loading && (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <p className="text-gray-500">No features found</p>
        </div>
      )}
    </div>
  );
};

export default FeatureFlagsComponent;

